'use client'

import { FormError } from '@/components/forms/FormError'
import { FormItem } from '@/components/forms/FormItem'
import { Message } from '@/components/Message'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import Link from 'next/link'
import React, { Fragment, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'

type FormData = {
  email: string
}

export const ForgotPasswordForm: React.FC = () => {
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<FormData>()

  const onSubmit = useCallback(async (data: FormData) => {
    setIsSubmitting(true)
    const response = await fetch(
      `/api/users/forgot-password`,
      {
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      },
    )

    setIsSubmitting(false)
    if (response.ok) {
      setSuccess(true)
      setError('')
    } else {
      setError(
        'Er is een probleem opgetreden bij het versturen van de reset e-mail. Probeer het opnieuw.',
      )
    }
  }, [])

  return (
    <Fragment>
      {!success && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Message className="mb-6" error={error} />
          <div className="space-y-5">
            <FormItem>
              <Label htmlFor="email" className="text-sm font-medium text-foreground">
                E-mailadres
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="naam@voorbeeld.nl"
                autoFocus
                className="h-11 rounded-lg border-border/60 bg-background/50 transition-colors focus:border-primary/50 focus:ring-primary/20"
                {...register('email', { required: 'E-mailadres is verplicht.' })}
              />
              {errors.email && <FormError message={errors.email.message} />}
            </FormItem>
          </div>

          <div className="mt-8 space-y-3">
            <Button
              className="w-full h-11 rounded-lg font-medium"
              disabled={isSubmitting}
              size="lg"
              type="submit"
              variant="default"
            >
              {isSubmitting ? 'Bezig met versturen...' : 'Reset link versturen'}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border/60" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card/50 px-3 text-muted-foreground">of</span>
              </div>
            </div>

            <Button asChild variant="outline" size="lg" className="w-full h-11 rounded-lg font-medium">
              <Link href="/inloggen">Terug naar inloggen</Link>
            </Button>
          </div>
        </form>
      )}
      {success && (
        <div className="text-center">
          <h2 className="text-lg font-semibold text-foreground mb-4">E-mail verzonden!</h2>
          <p className="text-muted-foreground mb-6">
            Controleer je inbox voor een link om je wachtwoord veilig te resetten.
          </p>
          <Button asChild variant="outline">
            <Link href="/inloggen">Terug naar inloggen</Link>
          </Button>
        </div>
      )}
    </Fragment>
  )
}
